/**
 * Payment deep-link builders for the Join screen.
 *
 * These are CONVENIENCE LINKS only — the app never confirms payment.
 * The admin still marks entries paid in person. Don't add webhook or
 * auto-verify logic on top of these.
 *
 * Both apps accept slightly different URL shapes; the schemes below are
 * the documented public-link variants that work from any browser without
 * the app installed:
 *   - Venmo:  https://venmo.com/<handle>?txn=pay&amount=<amt>&note=<note>
 *   - PayPal: https://paypal.me/<handle>/<amt>
 *
 * Amount is the entry fee × number of entries being joined (a second
 * buy-in pays 2× the fee). Encoded as plain decimal dollars (10.00).
 * Note is URL-encoded.
 */

export type PaymentInput = {
  /** Per-entry fee in cents. */
  entryFeeCents: number;
  /** Number of entries being joined (1 for first entry, 2 for second, etc.). */
  entryCount: number;
  /** Machine name for the payment note. e.g. "Home Slots tournament entry". */
  machineName: string;
};

/** Round to two decimal places without floating-point noise. */
function dollars(cents: number, count: number): string {
  const total = cents * count;
  const whole = Math.trunc(total / 100);
  const fraction = Math.abs(total % 100);
  return `${whole}.${fraction.toString().padStart(2, '0')}`;
}

function note(machineName: string): string {
  return `${machineName} tournament entry`;
}

/**
 * Build a Venmo deep-link, or null if no handle is configured.
 * Caller hides the button when null.
 */
export function venmoLink(
  handle: string | null | undefined,
  input: PaymentInput,
): string | null {
  if (!handle) return null;
  const amt = dollars(input.entryFeeCents, input.entryCount);
  const encodedNote = encodeURIComponent(note(input.machineName));
  return `https://venmo.com/${encodeURIComponent(handle)}?txn=pay&amount=${amt}&note=${encodedNote}`;
}

/**
 * Build a PayPal.me deep-link, or null if no handle is configured.
 * Caller hides the button when null.
 *
 * PayPal.me doesn't support a memo field in the URL; the note is omitted
 * by design. The amount is encoded as `/<amt>` per PayPal's published
 * format.
 */
export function paypalLink(
  handle: string | null | undefined,
  input: PaymentInput,
): string | null {
  if (!handle) return null;
  const amt = dollars(input.entryFeeCents, input.entryCount);
  return `https://paypal.me/${encodeURIComponent(handle)}/${amt}`;
}

/** Exported for tests; not used by callers. */
export const _internals = { dollars, note };
